import React, { useCallback, useEffect, useState } from 'react';
import { Search, Users, Clock3, CheckCircle2, X } from 'lucide-react';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { listInvitedVisitors } from '../../services/staffVisitorService';
import { approveInvite } from '../../services/guardService';
import { createCompanyInvite } from '../../services/companyService';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';

export default function InvitedVisitorsPage() {
  const { user } = useAuth();
  const [invited, setInvited] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm();

  const fetchInvited = useCallback(async () => {
    setLoading(true);

    try {
      const res = await listInvitedVisitors({ search });
      if (res.success) {
        setInvited(res.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors du chargement des invités');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchInvited();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [fetchInvited]);

  const handleApprove = async (visitId) => {
    try {
      setActionLoading(visitId);
      const res = await approveInvite(visitId);
      if (res.success) {
        // Refresh list immediately
        fetchInvited();
        if (selectedVisitor && selectedVisitor.visit_id === visitId) {
            setSelectedVisitor(prev => ({...prev, entry_at: new Date().toISOString()}));
        }
      }
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Erreur lors de l\'approbation');
    } finally {
      setActionLoading(null);
    }
  };

  const onSubmit = async (data) => {
    setError('');
    setSuccess('');
    try {
      const payload = { ...data };
      const res = await createCompanyInvite(payload);
      if (res.success) {
        setSuccess('Invité enregistré avec succès. Un email avec son code QR lui a été envoyé.');
        reset();
        fetchInvited();
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erreur lors de l\'enregistrement de l\'invité');
    }
  };

  return (
    <div className="vip-visitors-page">
      <div className="staff-vip-layout">
        {user?.role === 'admin' && showForm && (
          <form onSubmit={handleSubmit(onSubmit)} className="staff-vip-form-card">
            <div className="staff-vip-form-header">
              <h2>Inviter quelqu'un</h2>
              <p>Remplissez ce formulaire pour générer un pass QR qui lui sera envoyé par email.</p>
            </div>

            {error && <div className="error-banner">{error}</div>}
            {success && <div className="success-banner">{success}</div>}

            <div className="staff-vip-form-grid">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Nom complet *</label>
                <input {...register('fullName', { required: 'Le nom est requis' })} placeholder="Nom du visiteur" />
                {errors.fullName && <span className="field-error">{errors.fullName.message}</span>}
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Email (pour l'envoi du QR) *</label>
                <input type="email" {...register('email', { required: 'L\'email est requis pour envoyer le pass' })} placeholder="invite@email.com" />
                {errors.email && <span className="field-error">{errors.email.message}</span>}
              </div>

              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Téléphone (optionnel)</label>
                <input type="tel" {...register('phone')} placeholder="+216 XX XXX XXX" />
              </div>

              <div className="form-group">
                <label>Date prévue *</label>
                <input type="date" {...register('expectedDate', { required: 'La date est requise' })} />
                {errors.expectedDate && <span className="field-error">{errors.expectedDate.message}</span>}
              </div>

              <div className="form-group">
                <label>Heure prévue (optionnelle)</label>
                <input type="time" {...register('expectedTime')} />
              </div>
            </div>

            <div className="form-group">
              <label>Remarques (optionnelles)</label>
              <textarea {...register('comments')} rows={3} placeholder="Détails supplémentaires..." />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer l\'invitation'}
              </button>
            </div>
          </form>
        )}

        <section className="staff-vip-list-card" style={(user?.role !== 'admin' || !showForm) ? { gridColumn: '1 / -1' } : {}}>
          <div className="staff-vip-list-header">
            <div>
              <h2>Liste des invités (Entreprises)</h2>
              <p>Visiteurs enregistrés par les entreprises résidentes.</p>
            </div>

            <div className="header-actions">
              {user?.role === 'admin' && (
                <button 
                  className="btn btn-primary" 
                  onClick={() => setShowForm(!showForm)}
                  style={{ padding: '8px 16px', fontSize: '0.875rem' }}
                >
                  {showForm ? 'Fermer' : '+ Ajouter Invitation'}
                </button>
              )}
              <label className="staff-vip-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </label>
            </div>
          </div>

          {error && <div className="error-banner">{error}</div>}

          {loading ? (
            <LoadingSkeleton lines={7} />
          ) : invited.length === 0 ? (
            <div className="staff-vip-empty">Aucun invité trouvé.</div>
          ) : (
            <div className="staff-vip-list">
              {invited.map((item) => (
                <article 
                  key={item.visit_id} 
                  className="staff-vip-item" 
                  onClick={() => setSelectedVisitor(item)}
                  style={{ cursor: 'pointer', transition: 'background-color 0.2s', ':hover': { backgroundColor: 'var(--color-sidebar-hover)' } }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div className="staff-vip-item-top" style={{ marginBottom: '0' }}>
                        <span className="staff-vip-pill" style={{ background: 'var(--primary)', color: 'white' }}>
                          <Users size={14} fill="currentColor" /> Invité
                        </span>
                        <span className={`staff-vip-status ${item.entry_at ? 'status-arrivé' : 'status-à-venir'}`}>
                          {item.entry_at ? (
                            <>
                              Arrivé <span style={{ fontSize: '12px', fontWeight: 'normal', opacity: 0.9 }}>
                                ({new Date(item.entry_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })})
                              </span>
                            </>
                          ) : 'À venir'}
                        </span>
                      </div>
                      <h3 style={{ margin: '4px 0 0 0' }}>{item.full_name}</h3>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      {selectedVisitor && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '1rem'
        }}>
          <div className="modal-content" style={{
            background: 'var(--surface)', width: '100%', maxWidth: '500px',
            borderRadius: '12px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <div className="modal-header" style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)'
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', color: 'var(--text-main)' }}>Détails du visiteur</h2>
                <h3 style={{ margin: '4px 0 0', fontSize: '1rem', color: 'var(--text-muted)', fontWeight: 'normal' }}>{selectedVisitor.full_name}</h3>
              </div>
              <button onClick={() => setSelectedVisitor(null)} className="btn-icon" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <X size={20} color="var(--text-muted)" />
              </button>
            </div>
            
            <div className="modal-body" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '0.9375rem', color: 'var(--text-main)' }}>
              {selectedVisitor.email && <p style={{ margin: 0 }}><strong>Email:</strong> {selectedVisitor.email}</p>}
              {selectedVisitor.phone && <p style={{ margin: 0 }}><strong>Téléphone:</strong> {selectedVisitor.phone}</p>}
              {selectedVisitor.host_company && <p style={{ margin: 0 }}><strong>Hôte:</strong> {selectedVisitor.host_company} {selectedVisitor.host_contact_name ? `(${selectedVisitor.host_contact_name})` : ''}</p>}
              {selectedVisitor.inviter_name && <p style={{ margin: 0 }}><strong>Invité par:</strong> {selectedVisitor.inviter_name}</p>}
              
              <div className="staff-vip-time" style={{ marginTop: '8px' }}>
                <Clock3 size={16} color="var(--text-muted)" />
                <span>Créé le: {new Date(selectedVisitor.created_at).toLocaleString('fr-FR')}</span>
              </div>
              
              {selectedVisitor.expected_at && (
                <div className="staff-vip-time" style={{ marginTop: '4px' }}>
                  <Clock3 size={16} color="var(--text-muted)" />
                  <span>Date prévue: {new Date(selectedVisitor.expected_at).toLocaleDateString('fr-FR')}</span>
                  {(new Date(selectedVisitor.expected_at).getHours() !== 0 || new Date(selectedVisitor.expected_at).getMinutes() !== 0) && (
                    <span style={{ marginLeft: '6px' }}>
                      Heure: {new Date(selectedVisitor.expected_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              )}
              
              {selectedVisitor.entry_at ? (
                <div className="staff-vip-time" style={{ marginTop: '4px' }}>
                  <CheckCircle2 size={16} color="#10B981" />
                  <span style={{ color: '#10B981', fontWeight: '500' }}>Arrivé le: {new Date(selectedVisitor.entry_at).toLocaleString('fr-FR')}</span>
                </div>
              ) : (
                <div className="staff-vip-time" style={{ marginTop: '4px' }}>
                  <Clock3 size={16} color="#F59E0B" />
                  <span style={{ color: '#F59E0B', fontWeight: '500' }}>À venir</span>
                </div>
              )}
              
              {selectedVisitor.comments && (
                <div style={{ marginTop: '8px', padding: '12px', background: 'var(--page-bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <p style={{ margin: 0, fontStyle: 'italic', color: 'var(--text-muted)' }}>{selectedVisitor.comments}</p>
                </div>
              )}
              
              {user?.role === 'security' && !selectedVisitor.entry_at && (
                <button
                  onClick={() => handleApprove(selectedVisitor.visit_id)}
                  disabled={actionLoading === selectedVisitor.visit_id}
                  className="btn btn-primary"
                  style={{
                    marginTop: '16px',
                    width: '100%',
                    justifyContent: 'center',
                    padding: '10px 16px',
                    background: '#10B981',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <CheckCircle2 size={18} />
                  {actionLoading === selectedVisitor.visit_id ? 'Approbation...' : 'Approuver l\'entrée'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
